import React, {createContext} from 'react';

import {TemplateType} from 'common/report/dashboards/types';
import {CurrentProjectResource} from 'common/report/model-resources/projects';
import {CurrentUserResource} from 'common/report/model-resources/users';

export enum OnboardingIndustry {
  B2B = `B2B / SaaS`,
  Consumer = `Consumer`,
  Social = `Social`,
  Media = `Media`,
  Gaming = `Gaming`,
  Education = `Education`,
  Healthcare = `Healthcare`,
  ECommerce = `E-Commerce`,
  Agency = `Agency`,
  Finance = `Finance`,
  Marketplace = `Marketplace`,
  Other = `Other`,
}

export enum EventsNames {
  AddToCart = `Add to Cart`,
  CompleteSearch = `Complete Search`,
  ContentView = `Content View`,
  CreateItem = `Create Item`,
  Engage = `Engage`,
  FinishCourse = `Finish Course`,
  FinishLevel = `Finish Level`,
  FinishModule = `Finish Module`,
  FundedAccount = `Funded Account`,
  PageView = `Page View`,
  Purchase = `Purchase`,
  RequestDemo = `Request Demo`,
  ScheduleAppointment = `Schedule Appointment`,
  SelectBank = `Select Bank`,
  Share = `Share`,
  SignUp = `Sign Up`,
  StartCourse = `Start Course`,
  StartLevel = `Start Level`,
  Transaction = `Transaction`,
  ViewItem = `View Item`,
  ViewCart = `View Cart`,
}

export const INDUSTRY_EVENTS = {
  b2b: {
    name: OnboardingIndustry.B2B,
    events: [EventsNames.SignUp, EventsNames.PageView, EventsNames.RequestDemo],
    templates: [TemplateType.KPI, TemplateType.FEATURE_LAUNCH, TemplateType.WEB_ANALYTICS],
  },
  consumer: {
    name: OnboardingIndustry.Consumer,
    events: [EventsNames.SignUp, EventsNames.PageView, EventsNames.CreateItem],
    templates: [TemplateType.KPI, TemplateType.FEATURE_LAUNCH, TemplateType.WEB_ANALYTICS],
  },
  social: {
    name: OnboardingIndustry.Social,
    events: [EventsNames.SignUp, EventsNames.PageView, EventsNames.Engage, EventsNames.Share],
    templates: [TemplateType.KPI, TemplateType.LIFECYCLE_COHORT_ANALYSIS, TemplateType.FEATURE_LAUNCH],
  },
  media: {
    name: OnboardingIndustry.Media,
    events: [EventsNames.SignUp, EventsNames.ContentView, EventsNames.Share, EventsNames.Purchase],
    templates: [TemplateType.KPI, TemplateType.FEATURE_LAUNCH, TemplateType.WEB_ANALYTICS],
  },
  gaming: {
    name: OnboardingIndustry.Gaming,
    events: [EventsNames.SignUp, EventsNames.StartLevel, EventsNames.FinishLevel],
    templates: [TemplateType.KPI, TemplateType.FEATURE_LAUNCH, TemplateType.WEB_ANALYTICS],
  },
  education: {
    name: OnboardingIndustry.Education,
    events: [EventsNames.SignUp, EventsNames.StartCourse, EventsNames.FinishModule, EventsNames.FinishCourse],
    templates: [TemplateType.KPI, TemplateType.FEATURE_LAUNCH, TemplateType.WEB_ANALYTICS],
  },
  healthcare: {
    name: OnboardingIndustry.Healthcare,
    events: [EventsNames.SignUp, EventsNames.CompleteSearch, EventsNames.ScheduleAppointment],
    templates: [TemplateType.WEB_ANALYTICS, TemplateType.KPI, TemplateType.FEATURE_LAUNCH],
  },
  ecommerce: {
    name: OnboardingIndustry.ECommerce,
    events: [EventsNames.ViewItem, EventsNames.AddToCart, EventsNames.ViewCart, EventsNames.Purchase],
    templates: [TemplateType.MARKETING_KPI, TemplateType.KPI, TemplateType.WEB_ANALYTICS],
  },
  agency: {
    name: OnboardingIndustry.Agency,
    events: [EventsNames.SignUp, EventsNames.PageView],
    templates: [TemplateType.KPI, TemplateType.WEB_ANALYTICS, TemplateType.MARKETING_KPI],
  },
  finance: {
    name: OnboardingIndustry.Finance,
    events: [EventsNames.SignUp, EventsNames.SelectBank, EventsNames.FundedAccount, EventsNames.Transaction],
    templates: [TemplateType.KPI, TemplateType.FEATURE_LAUNCH, TemplateType.WEB_ANALYTICS],
  },
  marketplace: {
    name: OnboardingIndustry.Marketplace,
    events: [EventsNames.SignUp, EventsNames.PageView],
    templates: [TemplateType.KPI, TemplateType.FEATURE_LAUNCH, TemplateType.WEB_ANALYTICS],
  },
  other: {
    name: OnboardingIndustry.Other,
    events: [EventsNames.SignUp, EventsNames.PageView],
    templates: [TemplateType.KPI, TemplateType.FEATURE_LAUNCH, TemplateType.WEB_ANALYTICS],
  },
};

export type IndustryKey = keyof typeof INDUSTRY_EVENTS;

export enum Step {
  Welcome = `welcome`,
  Events = `events`,
  Implement = `implement`,
  Explore = `explore`,
}

export type CompleteStep = {
  [key in Step]?: boolean;
};

export type CheckIntegrationResult = {is_integrated: boolean};

export const SetupGuideContext = createContext<{
  modalManager: {
    openModal: () => void;
    closeModal: () => void;
  };
  step: Step;
  setStep: React.Dispatch<React.SetStateAction<Step>>;
  setupGuideInfo: SetupGuideType;
  setSetupGuideInfo: React.Dispatch<React.SetStateAction<SetupGuideType>>;
  isIntegrated: boolean;
  setIsIntegrated: React.Dispatch<React.SetStateAction<boolean>>;
  completed: CompleteStep;
}>(null);

export const VisibilityContext = createContext<{
  visibility: boolean;
  setVisibility: React.Dispatch<React.SetStateAction<boolean>>;
}>(null);

export const AppContext = createContext<{
  currentProject: CurrentProjectResource;
  currentUser: CurrentUserResource;
}>({currentProject: null, currentUser: null});

export type BreakdownType = `device_type` | `country` | `os`;

export type BreakdownPreviewProps = {events: string[]; property: BreakdownType};

// Utility type
type SnakeToCamelCase<S extends string> = S extends `${infer T}_${infer U}`
  ? `${T}${Capitalize<SnakeToCamelCase<U>>}`
  : S;

type ImmersiveOnboardingRuntime = {
  [K in keyof SetupGuideResponse as SnakeToCamelCase<K>]: SetupGuideResponse[K];
};

// This is version-agnostic runtime type
export type SetupGuideType = Omit<ImmersiveOnboardingRuntime, `industry` | `stepsCompleted`> & {
  industry: IndustryKey;
  stepsCompleted: Set<Step>;
};

// API Response Type with primitive types
export type SetupGuideResponse = {
  author: string;
  email: string;
  events: string[];
  industry: string;
  modified: string;
  project_id: number;
  project_token: string;
  steps_completed: string[];
  events_properties_map?: {
    [event: string]: string[];
  };
};

export enum SubStep {
  Welcome = `welcome`,
  EventConfirmation = `event`,
  Segmentation = `property`,
  ReadyToConnect = `ready`,
}

export const UserEventPropertiesMapContext = createContext<{
  eventKeys: string[];
  setEventKeys: React.Dispatch<React.SetStateAction<string[]>>;
  currentEventPropertiesMap: Map<string, string[]>;
  setCurrentEventPropertiesMap: React.Dispatch<React.SetStateAction<Map<string, string[]>>>;
}>(null);
